import { useState } from "react";
import { motion } from "framer-motion";
import { MainNav } from "../components/MainNav";
import { useLanguage } from "../contexts/LanguageContext";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, FileText, Shield, ArrowLeft } from "lucide-react";
import { useIsMobile } from "../hooks/use-mobile";
import { useNavigate } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import { getProductsSchemaList } from "../lib/structuredData";

const ProductsPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [openProductIndex, setOpenProductIndex] = useState<number | null>(null);
  const [openFileIndex, setOpenFileIndex] = useState<number | null>(null);
  const isMobile = useIsMobile();

  const seoContent = {
    title: language === 'sv' 
      ? 'Våra produkter - HVO100, Diesel, Bensin | Petrobell'
      : 'Our Products - HVO100, Diesel, Petrol | Petrobell',
    description: language === 'sv'
      ? 'Petrobell erbjuder ett brett sortiment av marina drivmedel: HVO100, Diesel MK1, Bensin 98, Gasoil och mer. Ladda ner produktdatablad och säkerhetsdatablad.'
      : 'Petrobell offers a wide range of marine fuels: HVO100, Diesel MK1, Petrol 98, Gasoil and more. Download product data sheets and safety data sheets.',
    keywords: language === 'sv'
      ? 'HVO100, marina drivmedel, diesel MK1, bensin 98, gasoil, marina bränslen, bunkerbränsle produkter, miljödiesel'
      : 'HVO100, marine fuel, marine gasoil, diesel MK1, petrol 98, gasoil, marine fuels, bunker fuel products, eco diesel'
  };

  const products = [
    {
      name: "HVO100",
      files: [
        language === 'sv' ? "Produktdatablad" : "Product Data Sheet",
        language === 'sv' ? "Säkerhetsdatablad" : "Safety Data Sheet"
      ]
    },
    {
      name: language === 'sv' ? "Bensin 98" : "Petrol 98",
      files: [
        language === 'sv' ? "Produktdatablad" : "Product Data Sheet",
        language === 'sv' ? "Säkerhetsdatablad" : "Safety Data Sheet"
      ]
    },
    {
      name: language === 'sv' ? "Diesel utan RME" : "Diesel without RME",
      files: [
        language === 'sv' ? "Produktdatablad" : "Product Data Sheet",
        language === 'sv' ? "Säkerhetsdatablad" : "Safety Data Sheet"
      ]
    },
    {
      name: language === 'sv' ? "Diesel MK1 färgad" : "Diesel MK1 coloured",
      files: [
        language === 'sv' ? "Produktdatablad" : "Product Data Sheet",
        language === 'sv' ? "Säkerhetsdatablad" : "Safety Data Sheet"
      ]
    },
    {
      name: language === 'sv' ? "Gasoil E10 färgad" : "Gasoil E10 coloured",
      files: [
        language === 'sv' ? "Produktdatablad" : "Product Data Sheet",
        language === 'sv' ? "Säkerhetsdatablad" : "Safety Data Sheet"
      ]
    },
    {
      name: language === 'sv' ? "Gasoil E32 färgad" : "Gasoil E32 coloured",
      files: [
        language === 'sv' ? "Produktdatablad" : "Product Data Sheet",
        language === 'sv' ? "Säkerhetsdatablad" : "Safety Data Sheet"
      ]
    }
  ];

  const handleFileClick = (productIndex: number, fileIndex: number) => {
    setOpenProductIndex(productIndex);
    setOpenFileIndex(fileIndex);
  };

  const closeDialog = () => {
    setOpenProductIndex(null);
    setOpenFileIndex(null);
  };

  const getFileContent = (productIndex: number, fileIndex: number) => {
    if (productIndex === 0 && fileIndex === 0) {
      return (
        <div className="w-full h-[80vh]">
          <iframe 
            src="https://storage.googleapis.com/petrobell_hemsida_produkter/produktdatablad/HVO100.pdf" 
            className="w-full h-full border-0"
            title="HVO100 Produktdatablad"
          />
        </div>
      );
    }
    
    if (productIndex === 1 && fileIndex === 0) {
      return (
        <div className="w-full h-[80vh]">
          <iframe 
            src="https://storage.googleapis.com/petrobell_hemsida_produkter/produktdatablad/Bensin%2098.pdf" 
            className="w-full h-full border-0"
            title="Bensin 98 Produktdatablad"
          />
        </div>
      );
    }

    if (productIndex === 2 && fileIndex === 0) {
      return (
        <div className="w-full h-[80vh]">
          <iframe 
            src="https://storage.googleapis.com/petrobell_hemsida_produkter/produktdatablad/Diesel%20Ofa%CC%88rgad%202025-05.pdf" 
            className="w-full h-full border-0"
            title="Diesel utan RME Produktdatablad"
          />
        </div>
      );
    }

    if (productIndex === 3 && fileIndex === 0) {
      return (
        <div className="w-full h-[80vh]">
          <iframe 
            src="https://storage.googleapis.com/petrobell_hemsida_produkter/produktdatablad/Diesel%20Fa%CC%88rgad%202025-04.pdf" 
            className="w-full h-full border-0"
            title="Diesel MK1 färgad Produktdatablad"
          />
        </div>
      );
    }

    if (productIndex === 4 && fileIndex === 0) {
      return (
        <div className="w-full h-[80vh]">
          <iframe 
            src="https://storage.googleapis.com/petrobell_hemsida_produkter/produktdatablad/GasOil%20E10%202025-04.pdf" 
            className="w-full h-full border-0"
            title="Gasoil E10 färgad Produktdatablad"
          />
        </div>
      );
    }

    if (productIndex === 5 && fileIndex === 0) {
      return (
        <div className="w-full h-[80vh]">
          <iframe 
            src="https://storage.googleapis.com/petrobell_hemsida_produkter/produktdatablad/GasOil%20E32%202025-04.pdf" 
            className="w-full h-full border-0"
            title="Gasoil E32 färgad Produktdatablad"
          />
        </div>
      );
    }
    
    if (productIndex === 0 && fileIndex === 1) {
      return (
        <div className="w-full h-[80vh]">
          <iframe 
            src="https://storage.googleapis.com/petrobell_hemsida_produkter/s%C3%A4kerhetsdatablad/HVO100.pdf" 
            className="w-full h-full border-0"
            title="HVO100 Säkerhetsdatablad"
          />
        </div>
      );
    }
    
    if (productIndex === 1 && fileIndex === 1) {
      return (
        <div className="w-full h-[80vh]">
          <iframe 
            src="https://storage.googleapis.com/petrobell_hemsida_produkter/s%C3%A4kerhetsdatablad/Bensin%2098%20Extra.pdf" 
            className="w-full h-full border-0"
            title="Bensin 98 Säkerhetsdatablad"
          />
        </div>
      );
    }
    
    if (productIndex === 2 && fileIndex === 1) {
      return (
        <div className="w-full h-[80vh]">
          <iframe 
            src="https://storage.googleapis.com/petrobell_hemsida_produkter/s%C3%A4kerhetsdatablad/Diesel%20Ofa%CC%88rgad.pdf" 
            className="w-full h-full border-0"
            title="Diesel utan RME Säkerhetsdatablad"
          />
        </div>
      );
    }
    
    if (productIndex === 3 && fileIndex === 1) {
      return (
        <div className="w-full h-[80vh]">
          <iframe 
            src="https://storage.googleapis.com/petrobell_hemsida_produkter/s%C3%A4kerhetsdatablad/Eldningsolja%20Miljo%CC%88.pdf" 
            className="w-full h-full border-0"
            title="Diesel MK1 färgad Säkerhetsdatablad"
          />
        </div>
      );
    }
    
    if (productIndex === 4 && fileIndex === 1) {
      return (
        <div className="w-full h-[80vh]">
          <iframe 
            src="https://storage.googleapis.com/petrobell_hemsida_produkter/s%C3%A4kerhetsdatablad/Eldningsolja%20E10.pdf" 
            className="w-full h-full border-0"
            title="Gasoil E10 färgad Säkerhetsdatablad"
          />
        </div>
      );
    }
    
    if (productIndex === 5 && fileIndex === 1) {
      return (
        <div className="w-full h-[80vh]">
          <iframe 
            src="https://storage.googleapis.com/petrobell_hemsida_produkter/s%C3%A4kerhetsdatablad/Eldningsolja%20E32.pdf" 
            className="w-full h-full border-0"
            title="Gasoil E32 färgad Säkerhetsdatablad"
          />
        </div>
      );
    }
    
    return (
      <div className="flex flex-col items-center justify-center h-full p-6">
        <p className="text-lg text-gray-600 mb-4">
          {language === 'sv' 
            ? "Dokumentet är inte tillgängligt just nu." 
            : "Document is not available at the moment."}
        </p>
      </div>
    );
  };

  return (
    <div className="min-h-screen w-full relative">
      <SEOHead
        title={seoContent.title}
        description={seoContent.description}
        path="/products"
        language={language}
        keywords={seoContent.keywords}
        jsonLd={getProductsSchemaList(language)}
      />
      {/* Fullskärmsbakgrund */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/images/hav-bg.jpg')" }}
        role="img"
        aria-label={language === 'sv' ? 'Havsvy - Marina drivmedel från Petrobell' : 'Sea view - Marine fuels from Petrobell'}
      />
      <div className="fixed inset-0 bg-black/50" />
      
      
      <MainNav />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="min-h-screen w-full relative z-10"
      >
        <section className="py-20 pt-32">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              {/* Back button */}
              <motion.button
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors group"
              >
                <ArrowLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
                <span>{language === 'sv' ? 'Tillbaka' : 'Back'}</span>
              </motion.button>

              <motion.h2 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-4xl md:text-5xl font-bold text-white mb-12"
              >
                {language === 'sv' ? 'Våra Produkter' : 'Our Products'}
              </motion.h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product, productIndex) => (
                  <motion.div 
                    key={productIndex}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: productIndex * 0.1 }}
                    className="group relative bg-white/10 backdrop-blur-md rounded-2xl overflow-hidden border border-white/20 hover:border-white/40 hover:bg-white/15 transition-all duration-300 hover:shadow-2xl hover:shadow-primary/20"
                  >
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-white mb-6 group-hover:text-white/90 transition-colors">
                        {product.name}
                      </h3>
                      
                      <div className="space-y-3">
                        {product.files.map((file, fileIndex) => (
                          <button
                            key={fileIndex}
                            onClick={() => handleFileClick(productIndex, fileIndex)}
                            className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/20 border border-white/10 hover:border-white/30 transition-all duration-200 text-white/80 hover:text-white group/btn"
                          >
                            <div className="p-2 rounded-lg bg-white/10">
                              {fileIndex === 0 ? (
                                <FileText className="h-4 w-4 text-white" />
                              ) : (
                                <Shield className="h-4 w-4 text-white" />
                              )}
                            </div>
                            <span className="text-sm font-medium">{file}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </motion.div>

      <Dialog open={openProductIndex !== null} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto flex flex-col">
          <DialogTitle className="flex justify-between items-center">
            {openProductIndex !== null && openFileIndex !== null && (
              <span>{products[openProductIndex].name} - {products[openProductIndex].files[openFileIndex]}</span>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={closeDialog}
              className="h-6 w-6 rounded-full absolute right-4 top-4"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </DialogTitle>
          <div className="mt-4 overflow-y-auto">
            {openProductIndex !== null && openFileIndex !== null && 
              getFileContent(openProductIndex, openFileIndex)
            }
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;
